import { Init, Update } from '~/mvuReducer'
import { Hook } from '../hooks/Hook'

export type Toast = {
  type: 'error' | 'warning' | 'success' | 'info'
  content: string
  id?: string
  heading?: string
  linkUrl?: string
  linkText?: string
  iconUrl?: string
  // Without a duration, the toast will be have to be manually dismissed
  durationMs?: number
  localOnly?: boolean
}

export type State = {
  toasts: ScheduledToast[]
}

export type Action =
  | { action: 'add-toast'; toast: Toast }
  | { action: 'dismiss-toast'; toast: Toast }
  | { action: 'tick-update' }
export type Dispatch = (action: Action) => void

export type SideEffect = { effect: 'clear-toast'; type: string }

type ScheduledToast = Toast & { startedAtUnixMs: number }

export function initialize(toasts: Toast[]): Init<State, SideEffect, Action> {
  return {
    state: { toasts: toasts.map((toast) => ({ ...toast, startedAtUnixMs: Date.now() })) },
    sideEffects: [],
    subscriptions: [
      {
        type: 'tick',
        intervalMs: 1000,
        action: { action: 'tick-update' },
      },
    ],
  }
}

export function applyAction(state: State, action: Action): Update<State, SideEffect> {
  switch (action.action) {
    case 'add-toast': {
      const alreadyHaveMatchingToast = state.toasts.findIndex((t) => t.content === action.toast.content) >= 0
      const startedAtUnixMs = Date.now()

      const newToasts = alreadyHaveMatchingToast
        ? state.toasts.map((t) => (t.content === action.toast.content ? { ...t, startedAtUnixMs } : t))
        : [...state.toasts, { ...action.toast, localOnly: true, startedAtUnixMs }]

      return [{ ...state, toasts: newToasts }, []]
    }

    case 'dismiss-toast': {
      const toDismiss = action.toast
      const sideEffects = toDismiss.localOnly ? [] : [{ effect: 'clear-toast' as const, type: toDismiss.type }]

      return [
        {
          ...state,
          toasts: state.toasts.filter((f) => f.type !== toDismiss.type && f.content !== toDismiss.content),
        },
        sideEffects,
      ]
    }

    case 'tick-update': {
      return [{ ...state, toasts: state.toasts.filter(shouldStillBeShown) }, []]
    }
  }
}

export function makeSideEffectExecutor(hook: Hook): (effect: SideEffect, dispatch: Dispatch) => void {
  return (sideEffect: SideEffect, _dispatch: Dispatch) => {
    switch (sideEffect.effect) {
      case 'clear-toast':
        hook.pushEvent('clear_flash', { type: sideEffect.type })
        break
    }
  }
}

function shouldStillBeShown(toast: ScheduledToast): boolean {
  return typeof toast.durationMs !== 'number' || toast.durationMs > Date.now() - toast.startedAtUnixMs
}
